import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Sparkles, Sprout, MapPin, Zap, Layers, ArrowRight, CheckCircle2, Globe2, DollarSign, Compass, AlertCircle } from 'lucide-react';
import { BusinessIdea, AnalysisResult } from '@/src/types';
import { generateGrassrootsVentures } from '@/src/services/apiServices';

interface GrassrootsIdeatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialLocation?: string;
  preferredCurrency?: string;
  onSelectVentureForLaunchpad: (idea: BusinessIdea) => void;
  onSaveToIntel?: (idea: BusinessIdea) => void;
  onScrutinizeVenture?: (idea: BusinessIdea) => void;
}

export const GrassrootsIdeatorModal: React.FC<GrassrootsIdeatorModalProps> = ({
  isOpen,
  onClose,
  initialLocation = '',
  preferredCurrency = 'USD',
  onSelectVentureForLaunchpad,
  onSaveToIntel,
  onScrutinizeVenture
}) => {
  const [location, setLocation] = useState(initialLocation || '');
  const [surroundings, setSurroundings] = useState('');
  const [resources, setResources] = useState('');
  const [gaps, setGaps] = useState('');
  const [budgetLimit, setBudgetLimit] = useState<number>(1500);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [savedIdeaIds, setSavedIdeaIds] = useState<Set<string>>(new Set());

  // Quick chip helper triggers
  const surroundingsChips = [
    'Unpaved Road / High Bus Stage Traffic',
    'Frequent Power Outages / Off-Grid',
    'Near University / High Youth Density',
    'Rural Weekly Market Square',
    'Densely Populated Low-Income Suburb'
  ];

  const resourcesChips = [
    'Abundant Fresh Produce / Farm Crops',
    'High Solar Radiation / Off-Grid Solar',
    'Local Metalworking & Carpentry Workshops',
    'High Mobile Money & Smartphone Usage'
  ];

  const gapsChips = [
    'Lack of Cold Storage / Food Spoilage',
    'Slow Phone Charging & Internet Access',
    'High Cost of Clean Drinking Water',
    'Limited Agro-Processing Equipment'
  ];

  const addChip = (text: string, current: string, setter: (val: string) => void) => {
    if (current.includes(text)) return;
    setter(current ? `${current}, ${text}` : text);
  };

  const handleSynthesize = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!surroundings.trim() && !gaps.trim()) {
      setError('Please describe your surroundings or community gaps so Gemini can synthesize grounded ventures.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await generateGrassrootsVentures({
        location: location.trim() || 'Global Emerging Markets',
        surroundingsDescription: surroundings.trim(),
        availableResources: resources.trim() || 'General local workforce and community network',
        communityGaps: gaps.trim(),
        budgetLimit,
        preferredCurrency
      });

      setResult(res);
    } catch (err: any) {
      console.error('Grassroots Synthesis Error:', err);
      setError(err.message || 'Failed to synthesize grassroots ventures. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveIdea = (idea: BusinessIdea, idx: number) => {
    const ideaKey = idea.id || `${idea.title}-${idx}`;
    if (onSaveToIntel) {
      onSaveToIntel(idea);
      setSavedIdeaIds(prev => new Set(prev).add(ideaKey));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="grassroots-ideator-modal-wrapper" className="fixed top-16 inset-x-0 bottom-0 z-[50] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/75"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-4xl bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden max-h-[calc(92vh-2rem)] flex flex-col z-10 text-white shadow-2xl"
          >
            {/* Modal Header */}
            <header className="px-6 py-5 bg-[#322D29] text-white flex items-center justify-between shrink-0 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-[#F26522] text-white flex items-center justify-center shrink-0">
                  <Sprout className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase font-heading tracking-widest flex items-center gap-2">
                    eNvesta AI Ideator
                    <span className="text-[9px] bg-white/10 text-[#F26522] font-bold px-2 py-0.5 uppercase tracking-widest border border-white/10 rounded">
                      Observational Intelligence
                    </span>
                  </h3>
                  <p className="text-[11px] text-white/60 font-medium">
                    No search engine data needed — input your physical environment to synthesize grounded venture blueprints cross-referenced with global benchmarks.
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
                aria-label="Close Ideator"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6 custom-scrollbar bg-[#3a3430]">
              {/* Form Controls */}
              <form onSubmit={handleSynthesize} className="space-y-6 bg-[#322D29] p-6 border border-white/[0.06] rounded-xl text-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Location Node */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-[#F26522]" />
                      Locality / Target Town
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Mbarara Town, Uganda or Arequipa, Peru"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full px-4 py-3 bg-[#3a3430] border border-white/[0.08] rounded-lg text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-colors"
                    />
                  </div>

                  {/* Budget Selector */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-1.5">
                      <DollarSign className="w-3.5 h-3.5 text-[#F26522]" />
                      Capital Ceiling ({preferredCurrency})
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={100}
                        max={25000}
                        step={100}
                        value={budgetLimit}
                        onChange={(e) => setBudgetLimit(Number(e.target.value))}
                        className="flex-1 accent-[#F26522] cursor-pointer"
                      />
                      <span className="px-3 py-2 bg-[#3a3430] border border-white/10 text-xs font-bold text-white rounded-lg shrink-0 font-heading min-w-[90px] text-center">
                        {preferredCurrency} {budgetLimit.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Surroundings & Infrastructure Description */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-[#F26522]" />
                      1. Describe Surroundings & Infrastructure
                    </label>
                    <span className="text-[9px] font-medium text-white/40">Road conditions, foot traffic, power grid...</span>
                  </div>
                  <textarea
                    rows={3}
                    placeholder="e.g., Busy bus stop stage on an unpaved road. High commuter foot traffic between 6-9 AM and 5-8 PM. Power goes off 3 days a week. Mostly small grocery kiosks around..."
                    value={surroundings}
                    onChange={(e) => setSurroundings(e.target.value)}
                    className="w-full p-4 bg-[#3a3430] border border-white/[0.08] rounded-lg text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-colors resize-none"
                  />
                  {/* Quick Chips */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {surroundingsChips.map((chip, idx) => (
                      <button
                        key={`surroundings-chip-${chip}-${idx}`}
                        type="button"
                        onClick={() => addChip(chip, surroundings, setSurroundings)}
                        className="text-[9px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-white/5 hover:bg-[#F26522] hover:text-white text-white/70 border border-white/10 rounded transition-colors cursor-pointer"
                      >
                        + {chip}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Local Resources & Skills */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-[#F26522]" />
                      2. Available Local Resources & Skills
                    </label>
                    <span className="text-[9px] font-medium text-white/40">Raw materials, tools, solar, local labor...</span>
                  </div>
                  <textarea
                    rows={2}
                    placeholder="e.g., Abundant plantains & cassava from surrounding farms, strong solar radiation, experienced local carpenters, mobile money agents present..."
                    value={resources}
                    onChange={(e) => setResources(e.target.value)}
                    className="w-full p-4 bg-[#3a3430] border border-white/[0.08] rounded-lg text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-colors resize-none"
                  />
                  {/* Quick Chips */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {resourcesChips.map((chip, idx) => (
                      <button
                        key={`resources-chip-${chip}-${idx}`}
                        type="button"
                        onClick={() => addChip(chip, resources, setResources)}
                        className="text-[9px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-white/5 hover:bg-[#F26522] hover:text-white text-white/70 border border-white/10 rounded transition-colors cursor-pointer"
                      >
                        + {chip}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Community Pain Points & Unmet Gaps */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 flex items-center gap-1.5">
                      <Compass className="w-3.5 h-3.5 text-[#F26522]" />
                      3. Unmet Community Gaps & Pain Points
                    </label>
                    <span className="text-[9px] font-medium text-white/40">What is missing or frustrating locally?</span>
                  </div>
                  <textarea
                    rows={2}
                    placeholder="e.g., Farmers lose 30% of produce to spoilage due to lack of cold storage; villagers travel 5km to charge phones or print documents..."
                    value={gaps}
                    onChange={(e) => setGaps(e.target.value)}
                    className="w-full p-4 bg-[#3a3430] border border-white/[0.08] rounded-lg text-xs font-medium text-white placeholder-white/30 outline-none focus:border-[#F26522] transition-colors resize-none"
                  />
                  {/* Quick Chips */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {gapsChips.map((chip, idx) => (
                      <button
                        key={`gaps-chip-${chip}-${idx}`}
                        type="button"
                        onClick={() => addChip(chip, gaps, setGaps)}
                        className="text-[9px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-white/5 hover:bg-[#F26522] hover:text-white text-white/70 border border-white/10 rounded transition-colors cursor-pointer"
                      >
                        + {chip}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-950/30 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-300 text-xs font-medium">
                    <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase tracking-widest text-xs rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-3 cursor-pointer"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      <span>Cross-Referencing Global Benchmarks...</span>
                    </>
                  ) : (
                    <>
                      <span>Synthesize Grassroots Venture Blueprints</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Generated Venture Results View */}
              {result && result.ideas && result.ideas.length > 0 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="flex items-center justify-between p-4 bg-[#322D29] border border-white/[0.06] rounded-xl text-white border-l-4 border-l-[#F26522]">
                    <div>
                      <h4 className="text-xs font-bold uppercase font-heading tracking-wider text-white">
                        Synthesized Grassroots Venture Blueprints ({result.ideas.length})
                      </h4>
                      <p className="text-[10px] text-white/60 font-medium mt-0.5">
                        Cross-referenced with global frugal innovation benchmarks & matched to your physical surroundings.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {result.ideas.map((idea, idx) => {
                      const ideaKey = idea.id || `${idea.title}-${idx}`;
                      const isSaved = savedIdeaIds.has(ideaKey);

                      return (
                        <div
                          key={`grassroots-idea-${ideaKey}-${idx}`}
                          className="bg-[#322D29] border border-white/[0.06] rounded-xl p-6 space-y-5 hover:border-[#F26522]/50 transition-all duration-300 relative group text-white"
                        >
                          {/* Card Top Row */}
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-white/[0.06] pb-4">
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 text-[#F26522] bg-[#F26522]/10 border border-[#F26522]/30 rounded">
                                  {idea.category || 'Grassroots Venture'}
                                </span>
                                {idea.riskLevel && (
                                  <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                                    idea.riskLevel === 'Low' ? 'text-emerald-400 bg-emerald-950/30 border-emerald-500/30' :
                                    idea.riskLevel === 'Medium' ? 'text-amber-400 bg-amber-950/30 border-amber-500/30' :
                                    'text-red-400 bg-red-950/30 border-red-500/30'
                                  }`}>
                                    {idea.riskLevel} Risk
                                  </span>
                                )}
                              </div>
                              <h3 className="text-lg font-bold uppercase font-heading text-white tracking-tight">
                                {idea.title}
                              </h3>
                            </div>

                            {/* Startup Capital Estimate */}
                            {idea.estimatedStartupCost && (
                              <div className="text-left sm:text-right shrink-0 p-3 bg-[#3a3430] border border-white/[0.06] rounded-lg">
                                <span className="text-[9px] font-bold uppercase tracking-widest text-white/50 block">Est. Capital Required</span>
                                <span className="text-sm font-bold text-white font-heading">{idea.estimatedStartupCost}</span>
                              </div>
                            )}
                          </div>

                          {/* Concept & Surroundings Fit */}
                          <div className="space-y-3">
                            <p className="text-xs text-white/80 font-medium leading-relaxed">
                              {idea.description}
                            </p>

                            {/* Global Benchmark Case Study */}
                            {idea.culturalSignificance && (
                              <div className="p-3.5 bg-[#3a3430] border border-white/[0.06] rounded-lg flex items-start gap-2.5">
                                <Globe2 className="w-4 h-4 text-[#F26522] shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] block font-heading">
                                    Global Benchmark Precedent
                                  </span>
                                  <p className="text-[11px] text-white/70 font-medium leading-snug mt-0.5">
                                    {idea.culturalSignificance}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Growth Potential */}
                            {idea.growthPotential && (
                              <div className="text-xs text-white/70 font-medium p-3.5 bg-[#3a3430] border border-white/[0.06] rounded-lg">
                                <strong className="font-bold text-white">Growth Strategy: </strong>
                                {idea.growthPotential}
                              </div>
                            )}
                          </div>

                          {/* Action Footer */}
                          <div className="pt-2 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06]">
                            <div className="flex items-center gap-2">
                              {onSaveToIntel && (
                                <button
                                  type="button"
                                  onClick={() => handleSaveIdea(idea, idx)}
                                  disabled={isSaved}
                                  className={`text-[10px] font-bold uppercase tracking-wider px-3.5 py-2 rounded-lg border transition-colors flex items-center gap-1.5 cursor-pointer ${
                                    isSaved
                                      ? 'bg-emerald-950/40 text-emerald-400 border-emerald-500/40'
                                      : 'bg-white/5 hover:bg-white/10 text-white/80 border-white/10'
                                  }`}
                                >
                                  {isSaved ? (
                                    <>
                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                      Saved to Intel
                                    </>
                                  ) : (
                                    <>
                                      + Save to Intel
                                    </>
                                  )}
                                </button>
                              )}

                              {onScrutinizeVenture && (
                                <button
                                  type="button"
                                  onClick={() => { onScrutinizeVenture(idea); onClose(); }}
                                  className="text-[10px] font-bold uppercase tracking-wider px-3.5 py-2 bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-lg transition-colors cursor-pointer"
                                >
                                  War Room Scrutiny
                                </button>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => { onSelectVentureForLaunchpad(idea); onClose(); }}
                              className="text-[10px] font-bold uppercase tracking-widest px-4 py-2.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white rounded-lg transition-colors flex items-center gap-2 ml-auto cursor-pointer"
                            >
                              <span>Analyze in Launchpad</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GrassrootsIdeatorModal;
